import { useEffect, useMemo, useState } from "react";
import api from "../api/axios";
import { Pencil, Trash2, X, Loader2 } from "lucide-react";


const ROLES = {
  admin: { label: "Administrator", color: "bg-red-50 border-red-200 text-red-700" },
  executive: { label: "Executive", color: "bg-purple-50 border-purple-200 text-purple-700" },
  member: { label: "Member", color: "bg-blue-50 border-blue-200 text-blue-700" },
  user: { label: "User (Read-only)", color: "bg-gray-50 border-gray-200 text-gray-700" },
};

const emptyForm = {
  email: "",
  full_name: "",
  role: "user",
  provisioned_by_admin: false,
};

const Modal = ({ title, children, onClose, size = "md" }) => {
  const sizeClass = {
    sm: "w-[400px]",
    md: "w-[560px]",
    lg: "w-[700px]",
  }[size];
  
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[2000]">
      <div className={`bg-white rounded-2xl shadow-xl p-6 ${sizeClass}`}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold">{title}</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-900">
            <X size={20} />
          </button>

        </div>
        {children}
      </div>
    </div>
  );
};

const AdminUsersPage = () => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [addOpen, setAddOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [deleteUser, setDeleteUser] = useState(null);
  

  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const fetchUsers = async () => {
    setLoading(true);
    setError("");
    try {
      const [usersRes, rolesRes] = await Promise.all([
        api.get("/api/admin/users/"),
        api.get("/api/admin/users/roles"),
      ]);
      setUsers(usersRes.data);
      setRoles(rolesRes.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load users");
      // If roles endpoint is admin-only and failed (403/401), fall back to local ROLES definition
      try {
        const fallback = Object.keys(ROLES).map((k) => ({ role: k, display_name: ROLES[k].label, description: "" }));
        setRoles(fallback);
      } catch (_) {
        // ignore
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const rows = useMemo(() => users || [], [users]);

  const openAdd = () => {
    setForm(emptyForm);
    setAddOpen(true);
  };

  const openEdit = (u) => {
    setForm({
      email: u.email,
      full_name: u.full_name || "",
      role: u.role || "member",
      provisioned_by_admin: !!u.provisioned_by_admin,
    });
    setEditUser(u);
  };

  // Invite flow removed: guests automatically gain access on SSO login

  const saveEdit = async () => {
    if (!editUser) return;
    setSaving(true);
    setError("");
    try {
      await api.put(`/api/admin/users/${editUser.id}/`, {
        full_name: form.full_name?.trim() || null,
        role: form.role,
        provisioned_by_admin: !!form.provisioned_by_admin,
      });
      setEditUser(null);
      await fetchUsers();
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to update user");
    } finally {
      setSaving(false);
    }
  };

  const saveAddUser = async () => {
    setSaving(true);
    setError("");
    try {
      await api.post("/api/admin/users/add", {
        email: form.email.trim(),
        role: form.role,
      });
      setAddOpen(false);
      setForm(emptyForm);
      await fetchUsers();
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to add user");
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    if (!deleteUser) return;
    setSaving(true);
    setError("");
    try {
      await api.delete(`/api/admin/users/${deleteUser.id}/`);
      setDeleteUser(null);
      await fetchUsers();
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to delete user");
    } finally {
      setSaving(false);
    }
  };

  // Invite-related helpers removed

  const toggleUserActive = async (u) => {
    setError("");
    try {
      await api.put(`/api/admin/users/${u.id}/`, { is_active: !u.is_active });
      await fetchUsers();
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to update user status");
    }
  };


  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Users & Roles</h1>
          <p className="text-sm text-gray-600">Manage users and roles</p>

        </div>
        <div>
          <button
            onClick={openAdd}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg shadow"
          >
            Add User
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      <div className="bg-white rounded-2xl shadow overflow-hidden">
        <div className="overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-700">
              <tr>
                <th className="text-left px-5 py-3">Name</th>
                <th className="text-left px-5 py-3">Email</th>
                <th className="text-left px-5 py-3">Role</th>
                <th className="text-left px-5 py-3">Status</th>
                <th className="text-left px-5 py-3">Joined</th>
                <th className="text-right px-5 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {loading ? (
                <tr>
                  <td className="px-5 py-6" colSpan={6} style={{ textAlign: "center" }}>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "0.5rem" }}>
                      <Loader2 size={32} color="#3b82f6" style={{ animation: "spin 1s linear infinite" }} />
                      <span style={{ color: "#64748b", fontSize: "0.95rem" }}>Loading users...</span>
                    </div>
                    <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td className="px-5 py-6 text-gray-500" colSpan={6}>No users found.</td>
                </tr>
              ) : (
                rows.map((u) => {
                  const roleInfo = ROLES[u.role] || ROLES.member;
                  const joinDate = u.created_at ? new Date(u.created_at).toLocaleDateString() : "—";
                  
                  return (
                    <tr key={u.id} className="hover:bg-gray-50">
                      <td className="px-5 py-3 font-medium">{u.full_name || "—"}</td>
                      <td className="px-5 py-3 text-gray-600">{u.email}</td>
                      <td className="px-5 py-3">
                        <span className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold border ${roleInfo.color}`}>
                          {roleInfo.label}
                        </span>
                      </td>
                      <td className="px-5 py-3">
                        <button
                          onClick={() => toggleUserActive(u)}
                          className={`px-3 py-1 rounded-full text-xs font-semibold border ${
                            u.is_active
                              ? "bg-green-50 border-green-200 text-green-700"
                              : "bg-red-50 border-red-200 text-red-700"
                          }`}
                        >
                          {u.is_active ? "Active" : "Inactive"}
                        </button>
                      </td>
                      <td className="px-5 py-3 text-gray-600">{joinDate}</td>
                      <td className="px-5 py-3">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => openEdit(u)}
                            className="px-3 py-1 rounded-lg border hover:bg-gray-50 inline-flex items-center gap-2 text-sm"
                          >
                            <Pencil size={16} />
                            Edit
                          </button>
                          <button
                            onClick={() => setDeleteUser(u)}
                            className="px-3 py-1 rounded-lg border border-red-200 text-red-700 hover:bg-red-50 inline-flex items-center gap-2 text-sm"
                          >
                            <Trash2 size={16} />
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add User Modal */}
      {addOpen && (
        <Modal title="Add User" onClose={() => setAddOpen(false)} size="md">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
              <input
                type="email"
                className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="user@company.com"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
              <div className="space-y-2">
                {roles.map((r) => (
                  <label key={r.role} className="flex items-center gap-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                    <input
                      type="radio"
                      name="role"
                      value={r.role}
                      checked={form.role === r.role}
                      onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
                      className="w-4 h-4"
                    />
                    <div>
                      <div className="font-medium text-gray-900">{r.display_name}</div>
                      <div className="text-xs text-gray-500">{r.description}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-6">
            <button onClick={() => setAddOpen(false)} className="px-4 py-2 border rounded-lg">
              Cancel
            </button>
            <button
              onClick={saveAddUser}
              disabled={saving || !form.email}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-50"
            >
              {saving ? "Adding..." : "Add User"}
            </button>
          </div>
        </Modal>
      )}

      {/* Edit User Modal */}
      {editUser && (
        <Modal title="Edit User" onClose={() => setEditUser(null)}>
          <div className="space-y-4">
            <div>

              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <div className="p-3 bg-gray-50 rounded-lg text-gray-600">{form.email}</div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
              <input
                type="text"
                className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={form.full_name}
                onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
              <select
                value={form.role}
                onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
                className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {roles.map((r) => (
                  <option key={r.role} value={r.role}>
                    {r.display_name}
                  </option>
                ))}
              </select>
              {roles.find((r) => r.role === form.role) && (
                <p className="text-xs text-gray-500 mt-1">
                  {roles.find((r) => r.role === form.role).description}
                </p>
              )}

              <div className="mt-4 flex items-center gap-4">
                <div className="flex-1">
                  <div className="font-medium text-gray-900">Application Access</div>
                  <div className="text-xs text-gray-500">Indicates this user was created or provisioned by an administrator.</div>
                </div>
                <button
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, provisioned_by_admin: !f.provisioned_by_admin }))}
                  aria-pressed={!!form.provisioned_by_admin}
                  className={`w-14 h-8 flex items-center p-1 rounded-full transition-colors focus:outline-none ${form.provisioned_by_admin ? 'bg-green-500' : 'bg-gray-300'}`}
                >
                  <div className={`bg-white w-6 h-6 rounded-full shadow transform transition-transform ${form.provisioned_by_admin ? 'translate-x-6' : ''}`} />
                </button>
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-6">
            <button onClick={() => setEditUser(null)} className="px-4 py-2 border rounded-lg">
              Cancel
            </button>
            <button
              onClick={saveEdit}
              disabled={saving || !form.email}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-50"
            >
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </Modal>
      )}

      {/* Delete User Modal */}
      {deleteUser && (
        <Modal title="Delete User" onClose={() => setDeleteUser(null)} size="sm">
          <p className="text-gray-700">
            Are you sure you want to delete <span className="font-semibold">{deleteUser.full_name || deleteUser.email}</span>?
          </p>
          <p className="text-sm text-gray-500 mt-2">This action cannot be undone.</p>
          <div className="flex justify-end gap-3 mt-6">
            <button onClick={() => setDeleteUser(null)} className="px-4 py-2 border rounded-lg">
              Cancel
            </button>
            <button
              onClick={doDelete}
              disabled={saving}
              className="px-4 py-2 bg-red-600 text-white rounded-lg disabled:opacity-50"
            >
              {saving ? "Deleting..." : "Delete"}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default AdminUsersPage;

